---
title: Companies
summary: "The Companies page is the deployment's reference list of companies: customers, forwarders, brokers, shippers, consignees and our own firms. A manager corrects a company, removes a wrong spelling, or merges two rows that are one firm."
visibility: authenticated
audience: operator
section: Pages
order: 62
last_verified: 2026-10-30
owner: docs
routes: ['/admin/companies']
aliases: [counterparties, company list, customer list, merge companies, duplicate company, company spelling, legal name, our firms, carrier, affiliate]
tour:
  - anchor: companies.filters
    text: "Show the companies that hold one role. Our firms are the carrier and its affiliates."
  - anchor: companies.merged
    text: "Add the companies merged into another, each saying which one it now reads as."
  - anchor: companies.list
    text: "Every company with its names, roles, spellings and what names it. Open a name to correct it, remove a spelling or merge it."
    when_missing: "No company is on file yet."
---

# Companies

The Companies page holds the companies the business deals with: customers, forwarders, brokers,
shippers and consignees, and our own firms (the carrier and its affiliates). Party fields such as
a contract's customer link to a row here, so one firm keeps one name however people type it. Open
the page from **Admin**, then **Companies**. It needs the Manage Companies permission and shows only
on a deployment whose work uses companies.

## The list

Each row shows the company's name, short name, legal name, country and roles, its other spellings
folded under a count, and what names it ("3 contracts, 1 intake link"). The chips above the list
show one role at a time; **Our firms** shows the carrier and its affiliates, and **All** shows
everyone again.

A company is added from a party field: typing a new name there offers to add it. A spelling is
added the same way, when someone picks a company for text that is not yet one of its names, so the
next person finds it first.

## Correcting a company

Open a company's name to edit its name, short name, legal name, country (two letters, such as BG),
registration and roles, then press **Save**. If someone changed the company while you had it open,
the save is refused and the drawer says so: press **Reload** to read it again, then make your change.
A name, short name or legal name you change is kept as a spelling marked as a former name, so
whatever was linked under the old name stays linked.

A wrong spelling, one that points people to the wrong firm, is taken off with **Remove** beside it.

## Merging two rows that are one firm

When two rows are one firm, open the one to retire and press **Merge into**, pick the company to
keep, and confirm with **Merge**. The retired row's names and spellings become the kept company's
spellings and its roles join the kept company's. Nothing that names the retired row is rewritten: a
contract that named it reads as the kept company from then on. A merge cannot be undone here.

Tick **Show merged companies** to list merged rows too, each reading "merged into" the company it
became. A merged row's counts are its own; the company it was merged into already counts them.
